import type { Organisation } from '@/api/resources/organisations'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { organisationsQueryOptions } from '@/api/resources/organisations'
import { BuildingIcon } from '@/components/icons/building-icon'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useOrganisationFilterStore } from '@/stores/organisation-filter-store'

interface OrganisationFilterProps {
  multiSelect?: boolean
}

export function OrganisationFilter({
  multiSelect = true,
}: OrganisationFilterProps): React.ReactNode {
  const { data: organisations } = useQuery(organisationsQueryOptions())
  const { selectedOrganisationIds, setSelectedOrganisationIds } = useOrganisationFilterStore()

  if (!organisations || organisations.length <= 1) return null

  return (
    <OrganisationFilterView
      organisations={organisations}
      selectedIds={selectedOrganisationIds}
      onSelectionChange={setSelectedOrganisationIds}
      multiSelect={multiSelect}
    />
  )
}

interface OrganisationFilterViewProps {
  organisations: Organisation[]
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
  multiSelect?: boolean
}

export function OrganisationFilterView({
  organisations,
  selectedIds,
  onSelectionChange,
  multiSelect = true,
}: OrganisationFilterViewProps): React.ReactNode {
  function toggleOrganisation(id: number): void {
    if (multiSelect) {
      if (selectedIds.includes(id)) {
        onSelectionChange(selectedIds.filter((existingId) => existingId !== id))
      } else {
        onSelectionChange([...selectedIds, id])
      }
    } else {
      onSelectionChange(selectedIds.includes(id) ? [] : [id])
    }
  }

  function getLabel(): string {
    if (selectedIds.length === 0) return multiSelect ? 'All organisations' : 'Select organisation'
    if (selectedIds.length === 1) {
      const organisation = organisations.find((o) => o.id === selectedIds[0])
      return organisation?.name ?? (multiSelect ? 'All organisations' : 'Select organisation')
    }
    return `${selectedIds.length} organisations`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <BuildingIcon className="size-4" />
          <span className="max-w-40 truncate">{getLabel()}</span>
          <ChevronsUpDown className="size-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {organisations.map((organisation) => {
          const isSelected = selectedIds.includes(organisation.id)
          return (
            <DropdownMenuItem
              key={organisation.id}
              onClick={(e) => {
                e.preventDefault()
                toggleOrganisation(organisation.id)
              }}
              className="gap-2"
            >
              <Check className={`size-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
              {organisation.name}
            </DropdownMenuItem>
          )
        })}
        {selectedIds.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onSelectionChange([])}>Clear filter</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
